import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ProgramToolbarUndoRedoButtons from '../program/ProgramToolbarUndoRedoButtons';
import WorkspaceFindBar from '../shared/WorkspaceFindBar';
import { SearchHighlightedText, SearchTextareaMirror } from '../shared/SearchTextareaMirror';
import TodoCategoryCreateModal from './TodoCategoryCreateModal';
import TodoNoteItemAddModal from './TodoNoteItemAddModal';
import TodoRoutineCreateModal from './TodoRoutineCreateModal';
import { SortableTodoCategoryChip } from './SortableTodoCategoryChip';
import { SortableRow } from './TodoNoteSortableRow';
import { DEFAULT_TODO_CATEGORY_NAME } from './todoNoteRowShared';
import {
  TodoNoteCalendarItemHoverPop,
  TodoNoteCalendarItemPopContent,
} from './TodoNoteCalendarItemHoverPop';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft, TodoNoteItemView } from '../../types/todoNote';
import { sortTodoItemsDrafts, todoItemBucketKey } from '../../utils/todoNoteBucket';
import { toISODate } from '../../utils/date';
import { normalizeBudgetSearchQuery } from '../../utils/budgetNoteSearch';
import {
  buildTodoCategoryNoteSearchMatchId,
  buildTodoItemFieldSearchMatchId,
  buildTodoMemoBodySearchMatchId,
  collectTodoNoteSearchMatches,
  todoItemCalendarPopSearchHit,
} from '../../utils/todoNoteSearch';
import { scrollTodoNoteSearchMatchIntoView } from '../../utils/todoNoteTimetableScroll';
import {
  TodoShareRevisionConflictError,
  buildTodoShareSavePayload,
} from '../../services/todoShareService';
import { formatTodoSharePageTitle } from '../../utils/todoShareTitle';
import {
  applyTodoCategoryModalConfirm,
  mergeDuplicateTodoCategories,
} from '../../utils/todoNoteCategoryEdit';
import {
  expandTodoItemsForView,
  normalizeRoutineDayLogs,
  parseRoutineOccurrenceId,
  patchRoutineDayLog,
  toggleRoutineOccurrenceComplete,
} from '../../utils/todoRoutine';

const DEFAULT_TODO_CATEGORY_COLOR = '#ffffff';
const TODO_NOTE_SHARE_VIEW_MODE_STORAGE_KEY = 'cp.todoNote.share.viewMode';

type TodoNoteViewMode = 'timetable' | 'calendar';

function readStoredShareViewMode(): TodoNoteViewMode {
  try {
    const raw = window.localStorage.getItem(TODO_NOTE_SHARE_VIEW_MODE_STORAGE_KEY);
    if (raw === 'timetable' || raw === 'calendar') return raw;
  } catch {
    /* noop */
  }
  return 'timetable';
}

function generateId(): string {
  return crypto.randomUUID();
}

function snapshotKey(
  categories: TodoNoteCategoryDraft[],
  items: TodoNoteItemDraft[],
  deletedCategoryIds: string[],
  deletedItemIds: string[]
): string {
  return JSON.stringify({
    c: [...categories].sort((a, b) => a.id.localeCompare(b.id)),
    i: [...items].sort((a, b) => a.id.localeCompare(b.id)),
    dc: [...deletedCategoryIds].sort(),
    di: [...deletedItemIds].sort(),
  });
}

function applyDragReorder(
  items: TodoNoteItemDraft[],
  sortedDisplay: TodoNoteItemDraft[],
  activeId: string,
  overId: string
): TodoNoteItemDraft[] {
  const a = sortedDisplay.find((x) => x.id === activeId);
  const b = sortedDisplay.find((x) => x.id === overId);
  if (!a || !b) return items;
  const bk = todoItemBucketKey(a);
  if (bk !== todoItemBucketKey(b)) return items;
  const bucketMembers = sortTodoItemsDrafts(items.filter((it) => todoItemBucketKey(it) === bk));
  const oldIdx = bucketMembers.findIndex((x) => x.id === activeId);
  const newIdx = bucketMembers.findIndex((x) => x.id === overId);
  if (oldIdx < 0 || newIdx < 0) return items;
  const moved = arrayMove(bucketMembers, oldIdx, newIdx);
  const orderMap = new Map(moved.map((it, idx) => [it.id, idx]));
  return items.map((it) => (orderMap.has(it.id) ? { ...it, bucket_order: orderMap.get(it.id)! } : it));
}

function rebalanceBucketOrders(items: TodoNoteItemDraft[], bucketKey: string): TodoNoteItemDraft[] {
  const inBucket = items
    .filter((it) => todoItemBucketKey(it) === bucketKey)
    .sort((a, b) => a.bucket_order - b.bucket_order || a.id.localeCompare(b.id));
  const orderMap = new Map(inBucket.map((it, idx) => [it.id, idx]));
  return items.map((it) => (orderMap.has(it.id) ? { ...it, bucket_order: orderMap.get(it.id)! } : it));
}

type TodoWorkspaceHistoryState = {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds: string[];
  categoryInputByItemId: Record<string, string>;
};

function historyStateFingerprint(state: TodoWorkspaceHistoryState): string {
  return JSON.stringify({
    ws: snapshotKey(state.categories, state.items, state.deletedCategoryIds, state.deletedItemIds),
    ci: state.categoryInputByItemId,
  });
}

export type SharedTodoNoteEditorLoadResult = {
  title: string;
  revision: number;
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
};

export type SharedTodoNoteEditorProps = {
  /** 상단 보조 설명 */
  subtitle?: string;
  load: () => Promise<SharedTodoNoteEditorLoadResult>;
  save: (params: {
    expectedRevision: number;
    categories: TodoNoteCategoryDraft[];
    items: TodoNoteItemDraft[];
    deletedCategoryIds: string[];
    deletedItemIds: string[];
  }) => Promise<number>;
  onTitleChange?: (title: string) => void;
  headerActions?: React.ReactNode;
  /** 소유자 패널처럼 바깥에 헤더가 있을 때 중복 sticky 헤더를 줄임 */
  compactChrome?: boolean;
  /** false면 카테고리 생성(+) 숨김 — 링크로 들어온 비소유자용 */
  allowCategoryCreate?: boolean;
  /** false면 루틴+ FAB 숨김 */
  allowRoutineCreate?: boolean;
};

const SharedTodoNoteEditor: React.FC<SharedTodoNoteEditorProps> = ({
  subtitle,
  load,
  save,
  onTitleChange,
  headerActions,
  compactChrome = false,
  allowCategoryCreate = true,
  allowRoutineCreate = true,
}) => {
  const [title, setTitle] = useState('공유 ToDo');
  const [revision, setRevision] = useState(1);
  const [categories, setCategories] = useState<TodoNoteCategoryDraft[]>([]);
  const [items, setItems] = useState<TodoNoteItemDraft[]>([]);
  const [deletedCategoryIds, setDeletedCategoryIds] = useState<string[]>([]);
  const [deletedItemIds, setDeletedItemIds] = useState<string[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflictHint, setConflictHint] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [addDraft, setAddDraft] = useState<TodoNoteItemDraft | null>(null);
  const [addCategoryInput, setAddCategoryInput] = useState('');
  const [categoryInputByItemId, setCategoryInputByItemId] = useState<Record<string, string>>({});
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<TodoNoteViewMode>(readStoredShareViewMode);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
  const [hideCompletedTodos, setHideCompletedTodos] = useState(false);
  const [hideRoutineNotes, setHideRoutineNotes] = useState(false);
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [editOccurrenceYmd, setEditOccurrenceYmd] = useState<string | null>(null);
  const [editCompleteMark, setEditCompleteMark] = useState<'v' | 'x' | null>(null);
  const [editCompleteTouched, setEditCompleteTouched] = useState(false);
  const [editDraft, setEditDraft] = useState<TodoNoteItemDraft | null>(null);
  const [editCategoryInput, setEditCategoryInput] = useState('');
  const [routineAddOpen, setRoutineAddOpen] = useState(false);
  const [routineAddDraft, setRoutineAddDraft] = useState<TodoNoteItemDraft | null>(null);
  const [routineAddCategoryInput, setRoutineAddCategoryInput] = useState('');
  const [routineModalMode, setRoutineModalMode] = useState<'create' | 'edit'>('create');
  const [routineEditOccurrenceYmd, setRoutineEditOccurrenceYmd] = useState<string | null>(null);
  const [routineEditCompleteMark, setRoutineEditCompleteMark] = useState<'v' | 'x' | null>(null);
  const [routineEditCompleteTouched, setRoutineEditCompleteTouched] = useState(false);
  const [routineDeleteConfirmId, setRoutineDeleteConfirmId] = useState<string | null>(null);
  const [undoRedoAvail, setUndoRedoAvail] = useState({ canUndo: false, canRedo: false });
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);

  const baselineRef = useRef('');
  const onTitleChangeRef = useRef(onTitleChange);
  const loadRef = useRef(load);
  const hydrateGenRef = useRef(0);
  const todoNoteScrollBodyRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<TodoWorkspaceHistoryState[]>([]);
  const historyIndexRef = useRef(-1);
  const suspendHistoryRef = useRef(false);
  const pendingHistoryRef = useRef<TodoWorkspaceHistoryState | null>(null);
  const activeSearchMatchIdRef = useRef<string | null>(null);

  useEffect(() => {
    onTitleChangeRef.current = onTitleChange;
  }, [onTitleChange]);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);
  useEffect(() => {
    try {
      window.localStorage.setItem(TODO_NOTE_SHARE_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* noop */
    }
  }, [viewMode]);

  const hydrate = useCallback(async (opts?: { quiet?: boolean }): Promise<boolean> => {
    const gen = ++hydrateGenRef.current;
    if (!opts?.quiet) setLoading(true);
    setError('');
    setConflictHint('');
    try {
      const snap = await loadRef.current();
      if (gen !== hydrateGenRef.current) return false;
      setTitle(snap.title);
      onTitleChangeRef.current?.(snap.title);
      setRevision(snap.revision);
      let cats = snap.categories.map((c) => ({ ...c, note_memo: c.note_memo ?? '' }));
      if (!cats.some((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME)) {
        cats = [
          {
            id: generateId(),
            user_id: '',
            workspace_id: null,
            name: DEFAULT_TODO_CATEGORY_NAME,
            color: DEFAULT_TODO_CATEGORY_COLOR,
            sort_order: 0,
            note_memo: '',
          },
          ...cats.map((c, i) => ({ ...c, sort_order: i + 1 })),
        ];
      }
      const merged = mergeDuplicateTodoCategories({
        categories: cats,
        items: snap.items,
        deletedCategoryIds: [],
      });
      setCategories(merged.categories);
      setItems(merged.items);
      setDeletedCategoryIds(merged.deletedCategoryIds);
      setDeletedItemIds(merged.deletedItemIds);
      const inputMap: Record<string, string> = { ...merged.categoryInputPatches };
      for (const it of merged.items) {
        if (inputMap[it.id]) continue;
        const cat = merged.categories.find((c) => c.id === it.category_id);
        inputMap[it.id] = cat?.name ?? DEFAULT_TODO_CATEGORY_NAME;
      }
      setCategoryInputByItemId(inputMap);
      baselineRef.current = snapshotKey(merged.categories, merged.items, [], []);
      historyRef.current = [
        {
          categories: merged.categories,
          items: merged.items,
          deletedCategoryIds: merged.deletedCategoryIds,
          deletedItemIds: merged.deletedItemIds,
          categoryInputByItemId: inputMap,
        },
      ];
      historyIndexRef.current = 0;
      setUndoRedoAvail({ canUndo: false, canRedo: false });
      setEditItemId(null);
      setEditDraft(null);
      return true;
    } catch (e) {
      if (gen !== hydrateGenRef.current) return false;
      setError(e instanceof Error ? e.message : '불러오기에 실패했습니다.');
      return false;
    } finally {
      if (gen === hydrateGenRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (snapshotKey(categories, items, deletedCategoryIds, deletedItemIds) !== baselineRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [categories, items, deletedCategoryIds, deletedItemIds]);

  const dirty =
    snapshotKey(categories, items, deletedCategoryIds, deletedItemIds) !== baselineRef.current;

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [categories]
  );

  const categoryMap = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const filterCategory = useMemo(
    () =>
      filterCategoryId
        ? sortedCategories.find((c) => c.id === filterCategoryId) ?? null
        : null,
    [filterCategoryId, sortedCategories]
  );

  const displayItems = useMemo(() => {
    const today = toISODate(new Date());
    let list: TodoNoteItemView[] = expandTodoItemsForView(items, today);
    if (filterCategoryId != null) list = list.filter((it) => it.category_id === filterCategoryId);
    if (hideRoutineNotes) list = list.filter((it) => !it.routineParentId);
    if (hideCompletedTodos) list = list.filter((it) => !it.completed);
    return list;
  }, [items, filterCategoryId, hideCompletedTodos, hideRoutineNotes]);

  const todayYmd = toISODate(new Date());
  const sortedItems = useMemo(() => sortTodoItemsDrafts(items), [items]);

  // ---- 찾기 ----
  const searchMatches = useMemo(
    () =>
      collectTodoNoteSearchMatches({
        query: searchQuery,
        orderedItems: sortedItems,
        categories,
        categoryInputByItemId,
        filterCategoryId,
      }),
    [searchQuery, sortedItems, categories, categoryInputByItemId, filterCategoryId]
  );
  const activeMatch = searchMatches[activeMatchIndex] ?? null;
  const activeMatchId = activeMatch?.matchId ?? null;
  activeSearchMatchIdRef.current = activeMatchId;

  const runTodoSearch = useCallback(() => {
    const q = normalizeBudgetSearchQuery(searchDraft);
    setSearchQuery(q);
    setActiveMatchIndex(0);
  }, [searchDraft]);

  const goPrevTodoSearchMatch = useCallback(() => {
    if (!searchMatches.length) return;
    const currentId = activeSearchMatchIdRef.current;
    const currentIdx = currentId
      ? searchMatches.findIndex((m) => m.matchId === currentId)
      : activeMatchIndex;
    const base = currentIdx >= 0 ? currentIdx : activeMatchIndex;
    setActiveMatchIndex((base - 1 + searchMatches.length) % searchMatches.length);
  }, [searchMatches, activeMatchIndex]);

  const goNextTodoSearchMatch = useCallback(() => {
    if (!searchMatches.length) return;
    const currentId = activeSearchMatchIdRef.current;
    const currentIdx = currentId
      ? searchMatches.findIndex((m) => m.matchId === currentId)
      : activeMatchIndex;
    const base = currentIdx >= 0 ? currentIdx : activeMatchIndex;
    setActiveMatchIndex((base + 1) % searchMatches.length);
  }, [searchMatches, activeMatchIndex]);

  const clearTodoSearch = useCallback(() => {
    setSearchDraft('');
    setSearchQuery('');
    setActiveMatchIndex(0);
  }, []);

  const handleSearchDraftChange = useCallback((next: string) => {
    setSearchDraft(next);
    if (!next) {
      setSearchQuery('');
      setActiveMatchIndex(0);
    }
  }, []);

  const showSearchNavSuffix = Boolean(searchQuery);

  useEffect(() => {
    if (!searchQuery) return;
    if (!searchMatches.length) {
      setActiveMatchIndex(0);
      return;
    }
    const trackedId = activeSearchMatchIdRef.current;
    if (trackedId) {
      const idx = searchMatches.findIndex((m) => m.matchId === trackedId);
      if (idx >= 0) {
        setActiveMatchIndex(idx);
        return;
      }
    }
    setActiveMatchIndex((i) => Math.min(i, searchMatches.length - 1));
  }, [searchQuery, searchMatches]);

  useEffect(() => {
    if (!activeMatch) return;

    if (activeMatch.field === 'category_note') {
      if (activeMatch.categoryId && filterCategoryId !== activeMatch.categoryId) {
        setFilterCategoryId(activeMatch.categoryId);
      }
      if (viewMode !== 'timetable') setViewMode('timetable');
      return;
    }

    const itemId = activeMatch.itemId;
    if (!itemId) return;
    const item = items.find((x) => x.id === itemId);
    if (!item) return;

    if (hideCompletedTodos && item.completed) setHideCompletedTodos(false);
    if (hideRoutineNotes && item.is_routine) setHideRoutineNotes(false);
    if (filterCategoryId && item.category_id !== filterCategoryId) setFilterCategoryId(null);

    if (viewMode === 'calendar') {
      const iso = item.due_date?.trim();
      if (iso) {
        const [ys, ms] = iso.split('-');
        const y = Number(ys);
        const m = Number(ms);
        if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
          setCalendarMonth(new Date(y, m - 1, 1));
          setSelectedCalendarDate(iso);
        }
      }
    }
  }, [activeMatch, items, filterCategoryId, hideCompletedTodos, hideRoutineNotes, viewMode]);

  useEffect(() => {
    if (!activeMatchId || !searchQuery) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const scrollEl = todoNoteScrollBodyRef.current;
        if (!scrollEl) return;
        const el = scrollEl.querySelector(`[data-budget-search-match="${CSS.escape(activeMatchId)}"]`);
        if (el instanceof HTMLElement) {
          scrollTodoNoteSearchMatchIntoView(scrollEl, el);
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [activeMatchId, searchQuery, viewMode, filterCategoryId, hideCompletedTodos, displayItems.length]);

  // ---- 실행 취소 / 다시 실행 ----
  const isTypingFieldActive = () => {
    const active = document.activeElement;
    if (!active) return false;
    if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)) return false;
    return Boolean(active.closest('.cp-todo-note-page'));
  };

  const pushHistorySnapshot = useCallback((next: TodoWorkspaceHistoryState) => {
    const idx = historyIndexRef.current;
    const current = historyRef.current[idx];
    if (current && historyStateFingerprint(current) === historyStateFingerprint(next)) return;
    const base = historyRef.current.slice(0, idx + 1);
    base.push(next);
    historyRef.current = base.slice(-150);
    historyIndexRef.current = historyRef.current.length - 1;
    setUndoRedoAvail({
      canUndo: historyIndexRef.current > 0,
      canRedo: false,
    });
  }, []);

  const flushPendingHistory = useCallback(() => {
    if (loading || suspendHistoryRef.current) return;
    const pending = pendingHistoryRef.current;
    if (!pending) return;
    if (isTypingFieldActive()) return;
    pendingHistoryRef.current = null;
    pushHistorySnapshot(pending);
  }, [loading, pushHistorySnapshot]);

  useEffect(() => {
    if (loading) return;
    if (suspendHistoryRef.current) {
      suspendHistoryRef.current = false;
      return;
    }
    const next: TodoWorkspaceHistoryState = {
      categories,
      items,
      deletedCategoryIds,
      deletedItemIds,
      categoryInputByItemId,
    };
    if (isTypingFieldActive()) {
      pendingHistoryRef.current = next;
      return;
    }
    pushHistorySnapshot(next);
  }, [loading, categories, items, deletedCategoryIds, deletedItemIds, categoryInputByItemId, pushHistorySnapshot]);

  const applyHistoryState = (state: TodoWorkspaceHistoryState) => {
    suspendHistoryRef.current = true;
    setCategories(state.categories);
    setItems(state.items);
    setDeletedCategoryIds(state.deletedCategoryIds);
    setDeletedItemIds(state.deletedItemIds);
    setCategoryInputByItemId(state.categoryInputByItemId);
  };

  const handleUndo = () => {
    if (historyIndexRef.current <= 0) return;
    const nextIdx = historyIndexRef.current - 1;
    const next = historyRef.current[nextIdx];
    if (!next) return;
    historyIndexRef.current = nextIdx;
    setUndoRedoAvail({
      canUndo: nextIdx > 0,
      canRedo: nextIdx < historyRef.current.length - 1,
    });
    applyHistoryState(next);
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    const nextIdx = historyIndexRef.current + 1;
    const next = historyRef.current[nextIdx];
    if (!next) return;
    historyIndexRef.current = nextIdx;
    setUndoRedoAvail({
      canUndo: nextIdx > 0,
      canRedo: nextIdx < historyRef.current.length - 1,
    });
    applyHistoryState(next);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const categoryDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setConflictHint('');
    try {
      const newRev = await save({
        expectedRevision: revision,
        categories,
        items,
        deletedCategoryIds,
        deletedItemIds,
      });
      const keptCats = categories.filter((c) => !deletedCategoryIds.includes(c.id));
      const keptItems = items.filter((it) => !deletedItemIds.includes(it.id));
      setCategories(keptCats);
      setItems(keptItems);
      setDeletedCategoryIds([]);
      setDeletedItemIds([]);
      setRevision(newRev);
      baselineRef.current = snapshotKey(keptCats, keptItems, [], []);
    } catch (e) {
      if (e instanceof TodoShareRevisionConflictError) {
        const ok = await hydrate({ quiet: true });
        if (ok) {
          setConflictHint(
            '다른 사람이 먼저 저장해 서버 버전으로 맞췄습니다. 필요하면 다시 수정한 뒤 저장해 주세요.'
          );
        }
      } else {
        setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
      }
    } finally {
      setSaving(false);
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (parseRoutineOccurrenceId(String(active.id)) || parseRoutineOccurrenceId(String(over.id))) return;
    setItems((prev) =>
      applyDragReorder(prev, displayItems, String(active.id), String(over.id))
    );
  };

  const handleCategoryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCategories((prev) => {
      const sorted = [...prev].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
      const oldIndex = sorted.findIndex((c) => c.id === String(active.id));
      const newIndex = sorted.findIndex((c) => c.id === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(sorted, oldIndex, newIndex).map((c, i) => ({ ...c, sort_order: i }));
    });
  };

  const patchItem = (id: string, patch: Partial<TodoNoteItemDraft>) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  const patchViewItemFields = (viewId: string, patch: Partial<TodoNoteItemDraft>) => {
    const occ = parseRoutineOccurrenceId(viewId);
    const parentId = occ?.parentId ?? viewId;
    if (occ && (patch.due_date !== undefined || patch.due_kind !== undefined)) return;
    patchItem(parentId, patch);
  };

  const toggleViewComplete = (viewId: string) => {
    const occ = parseRoutineOccurrenceId(viewId);
    if (occ) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === occ.parentId ? toggleRoutineOccurrenceComplete(it, occ.ymd, todayYmd) : it
        )
      );
      return;
    }
    const item = items.find((it) => it.id === viewId);
    if (item) patchItem(viewId, { completed: !item.completed });
  };

  const removeViewItem = (viewId: string) => {
    const parentId = parseRoutineOccurrenceId(viewId)?.parentId ?? viewId;
    setItems((prev) => prev.filter((x) => x.id !== parentId));
    setDeletedItemIds((d) => (d.includes(parentId) ? d : [...d, parentId]));
  };

  const requestRemoveViewItem = (viewId: string) => {
    const parentId = parseRoutineOccurrenceId(viewId)?.parentId ?? viewId;
    const parent = items.find((x) => x.id === parentId);
    if (parent?.is_routine) {
      setRoutineDeleteConfirmId(parentId);
      return;
    }
    removeViewItem(viewId);
  };

  const openRoutineEdit = (parentId: string, occurrenceYmd?: string | null, view?: TodoNoteItemView | null) => {
    const parent = items.find((x) => x.id === parentId);
    if (!parent?.is_routine || !parent.routine) return;
    const ymd = (occurrenceYmd ?? '').trim().slice(0, 10) || null;
    setRoutineAddDraft({
      ...parent,
      due_kind: 'day',
      due_date: ymd ?? parent.due_date,
      completed: view?.completed ?? false,
    });
    setRoutineAddCategoryInput(
      parent.category_id
        ? categories.find((c) => c.id === parent.category_id)?.name ??
            categoryInputByItemId[parentId] ??
            ''
        : categoryInputByItemId[parentId] ?? ''
    );
    setRoutineEditOccurrenceYmd(ymd);
    setRoutineEditCompleteMark(view?.completeMark ?? (view?.completed ? 'v' : null));
    setRoutineEditCompleteTouched(false);
    setRoutineModalMode('edit');
    setRoutineAddOpen(true);
  };

  const closeRoutineModal = () => {
    setRoutineAddOpen(false);
    setRoutineAddDraft(null);
    setRoutineModalMode('create');
    setRoutineEditOccurrenceYmd(null);
    setRoutineEditCompleteMark(null);
    setRoutineEditCompleteTouched(false);
  };

  const duplicateItemBelow = useCallback(
    (sourceId: string) => {
      const anchor = items.find((x) => x.id === sourceId);
      if (!anchor) return;
      const newId = generateId();
      const categoryLabel = anchor.category_id
        ? categories.find((c) => c.id === anchor.category_id)?.name ??
          categoryInputByItemId[sourceId] ??
          ''
        : categoryInputByItemId[sourceId] ?? '';
      setItems((prev) => {
        const a = prev.find((x) => x.id === sourceId);
        if (!a) return prev;
        const dup: TodoNoteItemDraft = {
          ...a,
          id: newId,
          completed: false,
          is_favorite: false,
          bucket_order: a.bucket_order + 0.5,
        };
        return rebalanceBucketOrders([...prev, dup], todoItemBucketKey(dup));
      });
      setCategoryInputByItemId((m) => ({ ...m, [newId]: categoryLabel }));
      setSelectedItemId(newId);
    },
    [items, categories, categoryInputByItemId]
  );

  const openAdd = (opts?: { calendar?: boolean }) => {
    const dueDate =
      opts?.calendar && selectedCalendarDate ? selectedCalendarDate : toISODate(new Date());
    const selectedFilterCategory =
      filterCategoryId != null ? sortedCategories.find((c) => c.id === filterCategoryId) ?? null : null;
    const defaultCat =
      selectedFilterCategory ??
      sortedCategories.find((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME) ??
      sortedCategories[0] ??
      null;
    setAddCategoryInput(defaultCat?.name ?? DEFAULT_TODO_CATEGORY_NAME);
    setAddDraft({
      id: generateId(),
      user_id: '',
      workspace_id: null,
      category_id: defaultCat?.id ?? null,
      due_kind: 'day',
      due_date: dueDate,
      title: '',
      body: '',
      misc: '',
      completed: false,
      bucket_order: 0,
    });
  };

  const commitAdd = () => {
    if (!addDraft) return;
    const typed = addCategoryInput.trim();
    let catId = addDraft.category_id;
    let catName = typed || DEFAULT_TODO_CATEGORY_NAME;
    if (typed) {
      const existing = categories.find((c) => c.name.trim().toLowerCase() === typed.toLowerCase());
      if (existing) {
        catId = existing.id;
        catName = existing.name;
      } else if (allowCategoryCreate) {
        const created: TodoNoteCategoryDraft = {
          id: generateId(),
          user_id: '',
          workspace_id: null,
          name: typed,
          color: DEFAULT_TODO_CATEGORY_COLOR,
          sort_order: categories.length,
          note_memo: '',
        };
        setCategories((prev) => [...prev, created]);
        catId = created.id;
        catName = created.name;
      } else {
        // 링크 게스트: 존재하지 않는 카테고리명은 새로 만들지 않고 기본 카테고리로 매핑
        const fallback = categories.find((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME) ?? null;
        catId = fallback?.id ?? catId;
        catName = fallback?.name ?? catName;
      }
    }
    const nextItem = { ...addDraft, category_id: catId };
    const bk = todoItemBucketKey(nextItem);
    const maxOrder = items
      .filter((it) => todoItemBucketKey(it) === bk)
      .reduce((m, it) => Math.max(m, it.bucket_order), -1);
    const withOrder = { ...nextItem, bucket_order: maxOrder + 1 };
    setItems((prev) => [...prev, withOrder]);
    setCategoryInputByItemId((m) => ({ ...m, [withOrder.id]: catName }));
    setAddDraft(null);
  };

  const openEditItem = useCallback(
    (itemId: string) => {
      const occ = parseRoutineOccurrenceId(itemId);
      const parentId = occ?.parentId ?? itemId;
      const item = items.find((it) => it.id === parentId);
      if (!item) return;
      const view = displayItems.find((it) => it.id === itemId);
      const cat = item.category_id ? categories.find((c) => c.id === item.category_id) : null;
      const ymd = occ?.ymd ?? null;
      if (item.is_routine && item.routine) {
        openRoutineEdit(parentId, occ?.ymd ?? null, view ?? null);
        return;
      }
      setEditItemId(parentId);
      setEditOccurrenceYmd(ymd);
      setEditCompleteMark(view?.completeMark ?? (view?.completed ? 'v' : null));
      setEditCompleteTouched(false);
      setEditDraft({
        ...item,
        due_kind: 'day',
        due_date: ymd ?? item.due_date,
        completed: view?.completed ?? item.completed,
      });
      setEditCategoryInput(cat?.name ?? categoryInputByItemId[parentId] ?? DEFAULT_TODO_CATEGORY_NAME);
    },
    [items, categories, categoryInputByItemId, displayItems, openRoutineEdit]
  );

  const closeEditItem = useCallback(() => {
    setEditItemId(null);
    setEditOccurrenceYmd(null);
    setEditCompleteMark(null);
    setEditCompleteTouched(false);
    setEditDraft(null);
    setEditCategoryInput('');
  }, []);

  const patchEditDraft = useCallback((patch: Partial<TodoNoteItemDraft>) => {
    setEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  useEffect(() => {
    if (!editItemId) return;
    if (!items.some((it) => it.id === editItemId)) {
      setEditItemId(null);
      setEditDraft(null);
      setEditCategoryInput('');
    }
  }, [items, editItemId]);

  const commitEditItem = () => {
    if (!editDraft || !editItemId) return;
    const typed = editCategoryInput.trim();
    let catId = editDraft.category_id;
    let catName = typed;
    if (typed) {
      const existing = categories.find((c) => c.name.trim().toLowerCase() === typed.toLowerCase());
      if (existing) {
        catId = existing.id;
        catName = existing.name;
      } else if (allowCategoryCreate) {
        const created: TodoNoteCategoryDraft = {
          id: generateId(),
          user_id: '',
          workspace_id: null,
          name: typed,
          color: DEFAULT_TODO_CATEGORY_COLOR,
          sort_order: categories.length,
          note_memo: '',
        };
        setCategories((prev) => [...prev, created]);
        catId = created.id;
        catName = created.name;
      } else {
        const fallback = categories.find((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME) ?? null;
        catId = fallback?.id ?? catId;
        catName = fallback?.name ?? catName;
      }
    } else {
      const cur = catId ? categories.find((c) => c.id === catId) : null;
      catName = cur?.name ?? '';
    }
    if (editOccurrenceYmd) {
      setItems((prev) =>
        prev.map((x) => {
          if (x.id !== editItemId) return x;
          const base: TodoNoteItemDraft = {
            ...x,
            category_id: catId,
            title: editDraft.title,
            body: editDraft.body,
            misc: editDraft.misc,
            completed: false,
            is_routine: true,
          };
          if (!editCompleteTouched) return base;
          return patchRoutineDayLog(base, editOccurrenceYmd, {
            completedOverride: editDraft.completed,
          });
        })
      );
    } else {
      patchItem(editItemId, {
        category_id: catId,
        due_kind: editDraft.due_kind,
        due_date: editDraft.due_date,
        title: editDraft.title,
        body: editDraft.body,
        misc: editDraft.misc,
        completed: editDraft.completed,
      });
    }
    setCategoryInputByItemId((m) => ({ ...m, [editItemId]: catName }));
    closeEditItem();
  };

  const calendarItemsByDate = useMemo(() => {
    const map = new Map<string, TodoNoteItemView[]>();
    for (const it of displayItems) {
      const key = it.due_date;
      const arr = map.get(key) ?? [];
      arr.push(it);
      map.set(key, arr);
    }
    return map;
  }, [displayItems]);

  const calendarCells = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const startWeekday = first.getDay();
    const start = new Date(first);
    start.setDate(first.getDate() - startWeekday);
    const out: Date[] = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  }, [calendarMonth]);

  const todayKey = useMemo(() => toISODate(new Date()), []);

  const displayTitle = formatTodoSharePageTitle(title);

  if (loading) {
    return (
      <section
        className={`cp-todo-note-page cp-todo-share-editor${compactChrome ? ' is-compact' : ''}`}
        aria-busy="true"
      >
        <p className="cp-todo-note-muted">불러오는 중…</p>
      </section>
    );
  }

  const stickyTopChrome = (
        <div className="cp-todo-note-sticky-top">
          {headerActions || subtitle ? (
            <div className="cp-todo-share-top-actions">
              <div className="cp-todo-share-top-actions-left">
                {headerActions ??
                  (subtitle ? <p className="cp-todo-share-subtitle">{subtitle}</p> : null)}
              </div>
            </div>
          ) : null}

          <div className="cp-todo-note-header">
            <div className="cp-todo-note-title-row">
              <h2
                id="cp-todo-share-editor-title"
                className={`cp-todo-note-title${compactChrome ? ' cp-visually-hidden' : ''}`}
              >
                {displayTitle}
              </h2>
              <div className="cp-todo-note-title-row-controls">
                <div className="cp-todo-note-view-toggle" role="tablist" aria-label="뷰 전환">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'timetable'}
                    className={`cp-todo-note-view-btn${viewMode === 'timetable' ? ' is-active' : ''}`}
                    onClick={() => setViewMode('timetable')}
                  >
                    타임테이블
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === 'calendar'}
                    className={`cp-todo-note-view-btn${viewMode === 'calendar' ? ' is-active' : ''}`}
                    onClick={() => setViewMode('calendar')}
                  >
                    캘린더
                  </button>
                </div>
              </div>
            </div>
            <div className="cp-todo-note-header-actions">
              <WorkspaceFindBar
                searchDraft={searchDraft}
                onSearchDraftChange={handleSearchDraftChange}
                onSubmit={runTodoSearch}
                onClear={clearTodoSearch}
                onPrev={goPrevTodoSearchMatch}
                onNext={goNextTodoSearchMatch}
                showNavSuffix={showSearchNavSuffix}
                activeIndex={activeMatchIndex}
                totalMatches={searchMatches.length}
                inputAriaLabel="ToDo 노트 찾기"
                disabled={loading || saving}
              />
              <ProgramToolbarUndoRedoButtons
                canUndo={undoRedoAvail.canUndo}
                canRedo={undoRedoAvail.canRedo}
                disabled={saving}
                onUndo={handleUndo}
                onRedo={handleRedo}
              />
              <button
                type="button"
                className="cp-btn-save-all"
                disabled={saving || !dirty}
                onClick={() => void handleSave()}
              >
                {saving ? '저장 중…' : '저장하기'}
              </button>
            </div>
          </div>
          {error ? <p className="cp-todo-note-error">{error}</p> : null}
          {conflictHint ? (
            <p className="cp-todo-note-error" role="status">
              {conflictHint}
            </p>
          ) : null}

          <div className="cp-todo-note-toolbar cp-todo-note-toolbar--floating-mobile">
            <div className="cp-todo-note-chips" role="tablist" aria-label="카테고리 필터">
              <div className={`cp-todo-note-chip-row${filterCategoryId === null ? ' is-active' : ''}`}>
                <button
                  type="button"
                  role="tab"
                  className="cp-todo-note-chip-main"
                  aria-selected={filterCategoryId === null}
                  onClick={() => setFilterCategoryId(null)}
                >
                  전체
                </button>
              </div>
              <DndContext
                sensors={categoryDndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleCategoryDragEnd}
              >
                <SortableContext
                  items={sortedCategories.map((c) => c.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {sortedCategories.map((c) => (
                    <SortableTodoCategoryChip
                      key={c.id}
                      id={c.id}
                      name={c.name}
                      color={c.color}
                      active={filterCategoryId === c.id}
                      onSelect={() => setFilterCategoryId(c.id)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {allowCategoryCreate ? (
                <button
                  type="button"
                  className="cp-todo-note-chip cp-todo-note-chip-add"
                  onClick={() => setCategoryModalOpen(true)}
                  disabled={saving}
                >
                  +
                </button>
              ) : null}
            </div>
            <div className="cp-todo-note-toolbar-toggle-wrap">
              <div className="cp-annual-top-saturday-toggle" aria-label="루틴 블럭 숨기기 토글">
                <span className="cp-weekly-schedule-saturday-toggle-label">
                  <span className="cp-todo-note-toggle-label-full">루틴 블럭 숨기기</span>
                  <span className="cp-todo-note-toggle-label-short">루틴 블럭</span>
                </span>
                <button
                  type="button"
                  className={'team-auto-approve-toggle' + (hideRoutineNotes ? ' on' : '')}
                  onClick={() => setHideRoutineNotes((v) => !v)}
                  aria-pressed={hideRoutineNotes}
                  aria-label="루틴 블럭 숨기기"
                >
                  <span className="team-auto-approve-toggle-slider" data-on={!hideRoutineNotes}>
                    OFF
                  </span>
                  <span className="team-auto-approve-toggle-slider" data-on={hideRoutineNotes}>
                    ON
                  </span>
                </button>
              </div>
              <div className="cp-annual-top-saturday-toggle" aria-label="완료 블럭 숨기기 토글">
                <span className="cp-weekly-schedule-saturday-toggle-label">
                  <span className="cp-todo-note-toggle-label-full">완료 블럭 숨기기</span>
                  <span className="cp-todo-note-toggle-label-short">완료 블럭</span>
                </span>
                <button
                  type="button"
                  className={'team-auto-approve-toggle' + (hideCompletedTodos ? ' on' : '')}
                  onClick={() => setHideCompletedTodos((v) => !v)}
                  aria-pressed={hideCompletedTodos}
                  aria-label="완료 블럭 숨기기"
                >
                  <span className="team-auto-approve-toggle-slider" data-on={!hideCompletedTodos}>
                    OFF
                  </span>
                  <span className="team-auto-approve-toggle-slider" data-on={hideCompletedTodos}>
                    ON
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
  );

  const calendarMonthNavChrome =
    viewMode === 'calendar' ? (
      <div className="cp-todo-note-calendar-head-shell">
            <button
              type="button"
              className="cp-todo-note-calendar-nav"
              onClick={() => {
                setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
                setSelectedCalendarDate(null);
              }}
              aria-label="이전 달"
            >
              ‹
            </button>
            <button
              type="button"
              className="cp-todo-note-calendar-title cp-todo-note-calendar-title-btn"
              onClick={() => {
                const now = new Date();
                setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                setSelectedCalendarDate(toISODate(now));
              }}
              aria-label="오늘로 이동"
            >
              {calendarMonth.getFullYear()}년 {calendarMonth.getMonth() + 1}월
            </button>
            <button
              type="button"
              className="cp-todo-note-calendar-nav"
              onClick={() => {
                setCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
                setSelectedCalendarDate(null);
              }}
              aria-label="다음 달"
            >
              ›
            </button>
          </div>
        ) : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <section
        className={`cp-todo-note-page cp-todo-share-editor${compactChrome ? ' is-compact' : ''}${
          viewMode === 'calendar' ? ' is-calendar' : ''
        }`}
        aria-labelledby="cp-todo-share-editor-title"
        onBlurCapture={() => {
          window.setTimeout(() => {
            flushPendingHistory();
          }, 0);
        }}
      >
        {viewMode !== 'calendar' ? stickyTopChrome : null}
        <div
          ref={todoNoteScrollBodyRef}
          className={`cp-todo-note-scroll-body${viewMode === 'calendar' ? ' is-calendar' : ''}`}
        >
          {viewMode === 'calendar' ? (
            <>
              {stickyTopChrome}
              {calendarMonthNavChrome}
            </>
          ) : null}
          {viewMode === 'timetable' ? (
            <div className="cp-todo-note-timetable-horiz-inner">
              {filterCategoryId && filterCategory ? (
                <div className="cp-todo-note-category-memo-strip">
                  <label
                    htmlFor="cp-todo-share-category-memo-field"
                    className="cp-todo-note-category-memo-label"
                  >
                    {filterCategory.name} · 카테고리 메모
                  </label>
                  <div className="cp-todo-note-category-memo-field-wrap">
                    <SearchTextareaMirror
                      id="cp-todo-share-category-memo-field"
                      className="cp-todo-note-category-memo-field"
                      value={filterCategory.note_memo}
                      query={searchQuery}
                      activeMatchId={activeMatchId}
                      buildMatchId={(start) =>
                        buildTodoCategoryNoteSearchMatchId(filterCategoryId!, start)
                      }
                      onChange={(v) => {
                        setCategories((prev) =>
                          prev.map((c) =>
                            c.id === filterCategoryId ? { ...c, note_memo: v } : c
                          )
                        );
                      }}
                      placeholder="이 카테고리용 자유 메모를 입력하세요…"
                      rows={3}
                      disabled={saving}
                      ariaLabel="카테고리 메모"
                    />
                  </div>
                </div>
              ) : null}
              <div className="cp-todo-note-column-head" aria-hidden>
                <span className="cp-todo-note-colhead cp-todo-note-colhead--ord">순</span>
                <div className="cp-todo-note-due-cat-head">
                  <span className="cp-todo-note-colhead cp-todo-note-colhead--due">마감</span>
                  <span className="cp-todo-note-colhead cp-todo-note-colhead--cat">카테고리</span>
                </div>
                <span className="cp-todo-note-colhead cp-todo-note-colhead--memo">메모</span>
                <span className="cp-todo-note-colhead cp-todo-note-colhead--misc">기타</span>
                <span className="cp-todo-note-colhead cp-todo-note-colhead--act" />
              </div>
              <SortableContext
                items={displayItems.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="cp-todo-note-list">
                  {displayItems.length === 0 ? (
                    <div
                      className="cp-todo-note-row cp-todo-note-row--placeholder"
                      aria-hidden
                    >
                      <span className="cp-todo-note-complete is-placeholder" />
                      <div className="cp-todo-note-cells">
                        <div className="cp-todo-note-cell cp-todo-note-cell--ord">
                          <span className="cp-todo-note-ord-num">1</span>
                        </div>
                        <div className="cp-todo-note-due-cat-group">
                          <div className="cp-todo-note-cell cp-todo-note-cell--due">
                            <div className="cp-todo-note-due-stack">
                              <div className="cp-todo-note-due-year-line">
                                <span className="cp-todo-note-placeholder-dash">—</span>
                                <span className="cp-todo-note-due-year-suffix">년</span>
                              </div>
                              <div className="cp-todo-note-due-main">
                                <span className="cp-todo-note-placeholder-dash">마감</span>
                              </div>
                            </div>
                          </div>
                          <div className="cp-todo-note-cell cp-todo-note-cell--cat">
                            <span className="cp-todo-note-placeholder-dash">카테고리</span>
                          </div>
                        </div>
                        <div className="cp-todo-note-cell cp-todo-note-cell--memo">
                          <span className="cp-todo-note-placeholder-dash">메모</span>
                        </div>
                        <div className="cp-todo-note-cell cp-todo-note-cell--misc">
                          <span className="cp-todo-note-placeholder-dash">기타</span>
                        </div>
                        <div className="cp-todo-note-cell cp-todo-note-cell--act" />
                      </div>
                    </div>
                  ) : (
                    displayItems.map((it, idx) => {
                      const parentId = it.routineParentId ?? it.id;
                      const isRoutineOcc = Boolean(it.routineParentId);
                      return (
                      <SortableRow
                        key={it.id}
                        item={it}
                        ord={idx + 1}
                        categories={sortedCategories}
                        categoryInput={categoryInputByItemId[parentId] ?? categoryInputByItemId[it.id] ?? ''}
                        onCategoryInput={(v) => {
                          setCategoryInputByItemId((m) => ({ ...m, [parentId]: v }));
                          if (v.trim().length >= 2) {
                            const hit = sortedCategories.find((c) =>
                              c.name.toLowerCase().startsWith(v.trim().toLowerCase())
                            );
                            if (hit) patchViewItemFields(it.id, { category_id: hit.id });
                          }
                        }}
                        onPickCategory={(cid) => {
                          patchViewItemFields(it.id, { category_id: cid });
                          const c = cid ? sortedCategories.find((x) => x.id === cid) : null;
                          setCategoryInputByItemId((m) => ({
                            ...m,
                            [parentId]: c?.name ?? '',
                          }));
                        }}
                        onDueDate={(iso) => patchViewItemFields(it.id, { due_date: iso })}
                        onDueSwitchToMonthOnly={(iso) =>
                          patchViewItemFields(it.id, { due_kind: 'month', due_date: iso })
                        }
                        onDueSwitchToDay={(iso) =>
                          patchViewItemFields(it.id, { due_kind: 'day', due_date: iso })
                        }
                        onMemoPatch={(t, b) => patchViewItemFields(it.id, { title: t, body: b })}
                        onMisc={(v) => patchViewItemFields(it.id, { misc: v })}
                        onToggleComplete={() => toggleViewComplete(it.id)}
                        onRemove={() => requestRemoveViewItem(it.id)}
                        onDuplicate={isRoutineOcc ? undefined : () => duplicateItemBelow(it.id)}
                        onEditRoutine={isRoutineOcc ? () => openRoutineEdit(parentId) : undefined}
                        dragDisabled={it.completed || filterCategoryId !== null || saving || isRoutineOcc}
                        selected={selectedItemId === it.id}
                        onSelect={() => setSelectedItemId(it.id)}
                        searchQuery={searchQuery}
                        activeMatchId={activeMatchId}
                        completeMark={it.completeMark}
                        routineBadge={isRoutineOcc}
                      />
                      );
                    })
                  )}
                </div>
              </SortableContext>
            </div>
          ) : (
            <div className="cp-todo-note-calendar-wrap">
              <div className="cp-todo-note-calendar-grid cp-todo-note-calendar-grid--weekday" aria-hidden>
                {['일', '월', '화', '수', '목', '금', '토'].map((w) => (
                  <div key={w} className="cp-todo-note-calendar-weekday">
                    {w}
                  </div>
                ))}
              </div>
              <div className="cp-todo-note-calendar-grid">
                {calendarCells.map((cellDate) => {
                  const key = toISODate(cellDate);
                  const inMonth = cellDate.getMonth() === calendarMonth.getMonth();
                  const dayOfWeek = cellDate.getDay();
                  const isSunday = dayOfWeek === 0;
                  const isSaturday = dayOfWeek === 6;
                  const isHoliday = isSunday || isSaturday;
                  const isToday = key === todayKey;
                  const dayItems = (calendarItemsByDate.get(key) ?? []).filter(
                    (it) => it.title.trim().length > 0
                  );
                  const isDateSelected = selectedCalendarDate === key;
                  const useTwoColCalendarItems = dayItems.length >= 5;
                  const searchPinnedDayItems = searchQuery
                    ? dayItems.filter((it) => todoItemCalendarPopSearchHit(it, searchQuery))
                    : [];
                  const useSearchPopRow = searchPinnedDayItems.length > 1;
                  return (
                    <div
                      key={key}
                      className={`cp-todo-note-calendar-cell${inMonth ? ' is-current-month' : ' is-outside'}${
                        isHoliday ? ' cp-todo-note-calendar-cell--holiday' : ''
                      }${isToday ? ' is-today' : ''}${isDateSelected ? ' is-selected' : ''}${
                        useSearchPopRow ? ' has-search-pop-row' : ''
                      }`}
                      role="button"
                      tabIndex={inMonth ? 0 : -1}
                      aria-disabled={!inMonth}
                      aria-current={isToday ? 'date' : undefined}
                      onClick={() => {
                        if (!inMonth) return;
                        setSelectedCalendarDate(key);
                      }}
                      onKeyDown={(e) => {
                        if (!inMonth) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedCalendarDate(key);
                        }
                      }}
                    >
                      <div className="cp-todo-note-calendar-day-row">
                        <div
                          className={`cp-todo-note-calendar-day${isToday ? ' is-today' : ''}${
                            isSaturday
                              ? ' cp-todo-note-calendar-day--sat'
                              : isHoliday
                                ? ' cp-todo-note-calendar-day--holiday'
                                : ''
                          }`}
                        >
                          {cellDate.getDate()}
                        </div>
                      </div>
                      <div
                        className={`cp-todo-note-calendar-items${
                          useTwoColCalendarItems ? ' cp-todo-note-calendar-items--two-col' : ''
                        }`}
                      >
                        {dayItems.map((it) => {
                          const cat = it.category_id ? categoryMap.get(it.category_id) : null;
                          const catColor = cat?.color?.trim() || '#c0c4cc';
                          const calendarSearchPinned =
                            Boolean(searchQuery) && todoItemCalendarPopSearchHit(it, searchQuery);
                          return (
                            <div
                              key={it.id}
                          className={`cp-todo-note-calendar-item${it.completed ? ' is-completed' : ''}${
                            it.completeMark === 'x' ? ' is-auto-missed' : ''
                          }${it.routineParentId ? ' is-routine' : ''}${
                                isDateSelected ? ' is-clickable' : ''
                              }${calendarSearchPinned ? ' cp-todo-note-calendar-item--search-hit' : ''}`}
                              style={{ ['--todo-cal-cat-color' as string]: catColor }}
                              role={isDateSelected ? 'button' : undefined}
                              tabIndex={isDateSelected ? 0 : -1}
                              onClick={(e) => {
                                if (!isDateSelected) return;
                                e.stopPropagation();
                                openEditItem(it.id);
                              }}
                              onKeyDown={(e) => {
                                if (!isDateSelected) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openEditItem(it.id);
                                }
                              }}
                            >
                              <span
                                className="cp-todo-note-calendar-item-dot"
                                style={{ background: catColor }}
                              />
                              <span className="cp-todo-note-calendar-item-title">
                                {searchQuery ? (
                                  <SearchHighlightedText
                                    text={it.title}
                                    query={searchQuery}
                                    activeMatchId={activeMatchId}
                                    buildMatchId={(start) =>
                                      buildTodoItemFieldSearchMatchId(it.id, 'memo', start)
                                    }
                                  />
                                ) : (
                                  it.title
                                )}
                              </span>
                              {!useSearchPopRow ? (
                                <TodoNoteCalendarItemHoverPop pinned={calendarSearchPinned}>
                                  <TodoNoteCalendarItemPopContent
                                    item={it}
                                    searchQuery={searchQuery}
                                    activeMatchId={activeMatchId}
                                  />
                                </TodoNoteCalendarItemHoverPop>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                      {useSearchPopRow ? (
                        <div className="cp-todo-note-calendar-cell-pops-row">
                          {searchPinnedDayItems.map((it) => (
                            <TodoNoteCalendarItemHoverPop key={it.id} inRow pinned>
                              <TodoNoteCalendarItemPopContent
                                item={it}
                                searchQuery={searchQuery}
                                activeMatchId={activeMatchId}
                              />
                            </TodoNoteCalendarItemHoverPop>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {allowCategoryCreate ? (
          <TodoCategoryCreateModal
            isOpen={categoryModalOpen}
            onClose={() => setCategoryModalOpen(false)}
            onConfirm={(payload) => {
              const next = applyTodoCategoryModalConfirm({
                categories,
                items,
                deletedCategoryIds,
                deletedItemIds,
                payload,
                defaultCategoryName: DEFAULT_TODO_CATEGORY_NAME,
                userId: '',
                workspaceId: null,
                generateId,
              });
              if (!next) {
                setCategoryModalOpen(false);
                return;
              }
              setCategories(next.categories);
              setItems(next.items);
              setDeletedCategoryIds(next.deletedCategoryIds);
              setDeletedItemIds(next.deletedItemIds);
              if (Object.keys(next.categoryInputPatches).length > 0) {
                setCategoryInputByItemId((prev) => ({ ...prev, ...next.categoryInputPatches }));
              }
              setCategoryModalOpen(false);
            }}
          />
        ) : null}

        <TodoNoteItemAddModal
          open={Boolean(addDraft)}
          draft={addDraft}
          categories={sortedCategories}
          categoryInput={addCategoryInput}
          onDismissBackdrop={() => setAddDraft(null)}
          onCategoryInput={setAddCategoryInput}
          onPickCategory={(id) => {
            setAddDraft((d) => (d ? { ...d, category_id: id } : d));
            const name = sortedCategories.find((c) => c.id === id)?.name;
            if (name) setAddCategoryInput(name);
          }}
          onDueDate={(iso) =>
            setAddDraft((d) => (d ? { ...d, due_date: iso, due_kind: 'day' } : d))
          }
          onDueSwitchToMonthOnly={(iso) =>
            setAddDraft((d) => (d ? { ...d, due_date: iso, due_kind: 'month' } : d))
          }
          onDueSwitchToDay={(iso) =>
            setAddDraft((d) => (d ? { ...d, due_date: iso, due_kind: 'day' } : d))
          }
          onMemoPatch={(t, b) => setAddDraft((d) => (d ? { ...d, title: t, body: b } : d))}
          onMisc={(v) => setAddDraft((d) => (d ? { ...d, misc: v } : d))}
          onCancel={() => setAddDraft(null)}
          onSave={commitAdd}
        />

        {editDraft ? (
          <TodoNoteItemAddModal
            open={Boolean(editDraft)}
            draft={editDraft}
            categories={sortedCategories}
            categoryInput={editCategoryInput}
            title="TODO 수정"
            saveLabel="확인"
            cancelLabel="닫기"
            autoFocusMemo={false}
            showHeaderCompleteToggle
            onToggleComplete={() => {
              setEditCompleteTouched(true);
              if (editDraft.completed) {
                patchEditDraft({ completed: false });
                setEditCompleteMark(null);
              } else {
                patchEditDraft({ completed: true });
                setEditCompleteMark('v');
              }
            }}
            onDismissBackdrop={closeEditItem}
            onCategoryInput={(v) => {
              setEditCategoryInput(v);
              if (v.trim().length >= 2) {
                const hit = sortedCategories.find((c) =>
                  c.name.toLowerCase().startsWith(v.trim().toLowerCase())
                );
                if (hit) patchEditDraft({ category_id: hit.id });
              }
            }}
            onPickCategory={(id) => {
              patchEditDraft({ category_id: id });
              const name = sortedCategories.find((c) => c.id === id)?.name;
              if (name) setEditCategoryInput(name);
            }}
            onDueDate={(iso) => {
              if (editOccurrenceYmd) return;
              patchEditDraft({ due_date: iso, due_kind: 'day' });
            }}
            onDueSwitchToMonthOnly={(iso) => {
              if (editOccurrenceYmd) return;
              patchEditDraft({ due_date: iso, due_kind: 'month' });
            }}
            onDueSwitchToDay={(iso) => {
              if (editOccurrenceYmd) return;
              patchEditDraft({ due_date: iso, due_kind: 'day' });
            }}
            onMemoPatch={(t, b) => patchEditDraft({ title: t, body: b })}
            onMisc={(v) => patchEditDraft({ misc: v })}
            onCancel={closeEditItem}
            onSave={commitEditItem}
            completeMark={editCompleteMark}
            onDelete={() => {
              if (!editItemId) return;
              removeViewItem(editItemId);
              closeEditItem();
            }}
          />
        ) : null}

        {routineDeleteConfirmId
          ? createPortal(
              <div
                className="cp-pin-confirm-overlay cp-pin-confirm-overlay--viewport"
                role="presentation"
                onClick={() => setRoutineDeleteConfirmId(null)}
              >
                <div
                  className="cp-pin-confirm cp-pin-confirm--warn"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="cp-todo-share-routine-delete-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-pin-confirm-warn-icon" aria-hidden>
                    !
                  </div>
                  <p id="cp-todo-share-routine-delete-title" className="cp-pin-confirm-warn-title">
                    루틴 삭제
                  </p>
                  <p className="cp-pin-confirm-warn-desc">
                    이 루틴 블럭을 삭제하면
                    <br />
                    모든 날짜의 루틴 블럭 기록이 함께 삭제됩니다.
                    <br />
                    계속하시겠습니까?
                  </p>
                  <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
                    <button
                      type="button"
                      className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                      onClick={() => setRoutineDeleteConfirmId(null)}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      className="cp-pin-modal-btn cp-pin-modal-btn--primary"
                      onClick={() => {
                        removeViewItem(routineDeleteConfirmId);
                        setRoutineDeleteConfirmId(null);
                      }}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}

        {allowRoutineCreate || routineAddOpen ? (
          <TodoRoutineCreateModal
            open={routineAddOpen}
            draft={routineAddDraft}
            categories={sortedCategories}
            categoryInput={routineAddCategoryInput}
            mode={routineModalMode}
            initialRoutine={routineModalMode === 'edit' ? routineAddDraft?.routine ?? null : null}
            occurrenceYmd={routineEditOccurrenceYmd}
            completeToggleMode={
              routineModalMode === 'edit' && routineEditOccurrenceYmd ? 'always' : 'never'
            }
            completeMark={routineEditCompleteMark}
            onToggleComplete={() => {
              if (!routineAddDraft) return;
              setRoutineEditCompleteTouched(true);
              if (routineAddDraft.completed) {
                setRoutineAddDraft((d) => (d ? { ...d, completed: false } : d));
                setRoutineEditCompleteMark(null);
              } else {
                setRoutineAddDraft((d) => (d ? { ...d, completed: true } : d));
                setRoutineEditCompleteMark('v');
              }
            }}
            onDelete={
              routineModalMode === 'edit' && routineAddDraft?.id
                ? () => {
                    const id = routineAddDraft.id;
                    closeRoutineModal();
                    setRoutineDeleteConfirmId(id);
                  }
                : undefined
            }
            onDismissBackdrop={closeRoutineModal}
            onCategoryInput={setRoutineAddCategoryInput}
            onPickCategory={(cid) => {
              setRoutineAddDraft((d) => (d ? { ...d, category_id: cid } : d));
              const name = cid ? sortedCategories.find((c) => c.id === cid)?.name : '';
              if (name) setRoutineAddCategoryInput(name);
            }}
            onMemoPatch={(title, body) => setRoutineAddDraft((d) => (d ? { ...d, title, body } : d))}
            onMisc={(v) => setRoutineAddDraft((d) => (d ? { ...d, misc: v } : d))}
            onCancel={closeRoutineModal}
            onSave={({ draft, routine }) => {
              if (routineModalMode === 'edit' && draft.id) {
                const prev = items.find((x) => x.id === draft.id);
                const prevLogs = prev?.routine?.dayLogs ?? {};
                let nextParent: TodoNoteItemDraft = {
                  ...(prev ?? draft),
                  title: draft.title,
                  body: draft.body,
                  misc: draft.misc,
                  category_id: draft.category_id,
                  is_routine: true,
                  completed: false,
                  routine: {
                    ...routine,
                    dayLogs: normalizeRoutineDayLogs(prevLogs),
                  },
                };
                if (routineEditOccurrenceYmd && routineEditCompleteTouched) {
                  nextParent = patchRoutineDayLog(nextParent, routineEditOccurrenceYmd, {
                    completedOverride: draft.completed,
                  });
                }
                patchItem(draft.id, {
                  title: nextParent.title,
                  body: nextParent.body,
                  misc: nextParent.misc,
                  category_id: nextParent.category_id,
                  is_routine: true,
                  completed: false,
                  routine: nextParent.routine,
                });
                const catName = draft.category_id
                  ? sortedCategories.find((c) => c.id === draft.category_id)?.name ?? ''
                  : '';
                setCategoryInputByItemId((m) => ({ ...m, [draft.id]: catName }));
                closeRoutineModal();
                return;
              }
              const newId = generateId();
              const categoryId = draft.category_id ?? sortedCategories[0]?.id ?? null;
              const finalItem: TodoNoteItemDraft = {
                ...draft,
                id: newId,
                category_id: categoryId,
                due_kind: 'day',
                due_date: toISODate(new Date()),
                completed: false,
                is_routine: true,
                routine,
                bucket_order: items.length,
              };
              setItems((prev) => [...prev, finalItem]);
              closeRoutineModal();
            }}
          />
        ) : null}

        <button
          type="button"
          className="cp-fab-save"
          onClick={() => void handleSave()}
          disabled={saving || !dirty}
          aria-label="저장하기"
          title={dirty ? '저장하기' : '저장할 변경사항 없음'}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
            alt=""
            className="cp-fab-save-icon"
          />
        </button>
        {allowRoutineCreate ? (
          <button
            type="button"
            className="cp-todo-note-fab-routine"
            onClick={() => {
              const baseCategory =
                (filterCategoryId ? sortedCategories.find((c) => c.id === filterCategoryId) : null) ??
                sortedCategories.find((c) => c.name.trim() === DEFAULT_TODO_CATEGORY_NAME) ??
                sortedCategories[0] ??
                null;
              setRoutineModalMode('create');
              setRoutineAddDraft({
                id: `__routine-draft-${generateId()}`,
                user_id: '',
                workspace_id: null,
                category_id: baseCategory?.id ?? null,
                due_kind: 'day',
                due_date: toISODate(new Date()),
                title: '',
                body: '',
                misc: '',
                completed: false,
                bucket_order: 0,
                is_routine: true,
              });
              setRoutineAddCategoryInput(baseCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME);
              setRoutineAddOpen(true);
            }}
            disabled={saving}
            aria-label="루틴 과제 추가"
            title="루틴 과제 추가"
          >
            <span className="cp-todo-note-fab-routine-label">루틴</span>
            <span className="cp-todo-note-fab-routine-plus" aria-hidden>
              +
            </span>
          </button>
        ) : null}
        <button
          type="button"
          className="cp-todo-note-fab-add"
          onClick={() => openAdd({ calendar: viewMode === 'calendar' })}
          disabled={saving}
          aria-label="블럭 추가"
          title="블럭 추가"
        >
          +
        </button>
      </section>
    </DndContext>
  );
};

export function sharedEditorSaveAdapter(
  saveFn: (args: {
    expectedRevision: number;
    payload: ReturnType<typeof buildTodoShareSavePayload>;
  }) => Promise<number>
): SharedTodoNoteEditorProps['save'] {
  return async ({ expectedRevision, categories, items, deletedCategoryIds, deletedItemIds }) =>
    saveFn({
      expectedRevision,
      payload: buildTodoShareSavePayload({
        categories,
        items,
        deletedCategoryIds,
        deletedItemIds,
      }),
    });
}

export default SharedTodoNoteEditor;
